/* eslint-disable max-lines */
import 'isomorphic-fetch';
import { updateClubRegistrationStatus } from './clubRegistration/actions';
import {
    AppShellLoginState,
    ClubIntegrationPixelResponse,
    ClubRegistrationStatus,
    FETCH_CLUB_INTEGRATION_PIXEL,
    FETCH_CLUB_INTEGRATION_PIXEL_FAILED,
    FETCH_CLUB_INTEGRATION_PIXEL_SUCCESS,
    IS_LOGGED_IN,
    IsLoggedInAction,
    IsRegisteredWithNewsletterActions,
    LD_SESSION_ID,
    LOGIN,
    LOGOUT,
    LOGOUT_FAILED,
    LOGOUT_SUCCESS,
    LOGIN_FAILED,
    LOGIN_SUCCESS,
    LOGIN_WITH_FACEBOOK,
    LoginAction,
    LoginFailedAction,
    LogoutAction,
    LogoutSuccessAction,
    LogoutFailedAction,
    LogoutResponse,
    LoginResponse,
    LoginWithFacebookAction,
    REGISTER_NEW_USER,
    RegisterNewUserAction,
    RegisterUserValues,
    RegisterUserValuesEmailPassword,
    SessionTypes,
    SET_IS_REGISTERED_WITH_NEWSLETTER,
    SET_REDIRECT_URL,
    SetRedirectUrlAction,
    UNSET_REDIRECT_URL,
    UnSetRedirectUrlAction,
    UserData,
    UserLoginDataResponse,
} from './types';
import identifyUser from './segmentIdentify';
import { DataFetchingThunkAction } from 'Client/redux/types';
import { fetchCartCount, clearCartAction } from 'Client/redux/cart/actions';
import { fetchOrAddLooksWishlist } from 'Client/redux/looksWishlist/actions';
import { fetchOrAddProductsWishlist, clearWishlistAction } from 'Client/redux/productsWishlist/actions';
import GaTracking from 'Common/ga';
import { trackUniversalAnalyticsSessionInit } from 'Client/redux/tracking/actions';
import { addNotification } from 'Client/redux/notifications/actions';
import { LOGIN_REGISTER_OVERLAY_EVENTS } from 'Common/ga/types';

const {
    REGISTRATION_OVERLAY_CATEGORY,
    REGISTRATION_SUCCESSFUL,
    NEW_REGISTRATION_W_NL,
    NEW_REGISTRATION_WO_NL,
    LOGIN_ACTION,
    LOGIN_FAILED_VIA_REG,
    LOGIN_PASSED_VIA_REG,
    LOGIN_OVERLAY_CATEGORY,
    PARTIAL_LOGIN_OVERLAY_CATEGORY,
} = LOGIN_REGISTER_OVERLAY_EVENTS;

const trackUserLoginAndRegistration = (data: UserLoginDataResponse | UserData, ga: GaTracking) => {
    return ga.trackUserLoginAndRegistration({
        'user-id': data.id || '',
        'user-name': data.firstName || '',
        'user-email': data.email || '',
        'user-createdAt': data.registeredAt || '',
        'user-hashed-mail': data.hashedEmail || '',
        'hashed-user-id': data.hashedId || '',
        'user-login_check': '1',
    });
};

const trackNewRegistration = (ga: GaTracking | undefined, isNewsletterSubscribed: boolean) => {
    if (!ga) {
        return;
    }

    if (isNewsletterSubscribed) {
        ga.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
            action: REGISTRATION_SUCCESSFUL,
            label: NEW_REGISTRATION_W_NL,
        });
    } else {
        ga.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
            action: REGISTRATION_SUCCESSFUL,
            label: NEW_REGISTRATION_WO_NL,
        });
    }
};

const trackLoginViaRegistration = (ga: GaTracking | undefined, loginStatus: boolean) => {
    if (!ga) {
        return;
    }

    if (loginStatus) {
        ga.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
            action: LOGIN_ACTION,
            label: LOGIN_PASSED_VIA_REG,
        });
    } else {
        ga.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
            action: LOGIN_ACTION,
            label: LOGIN_FAILED_VIA_REG,
        });
    }
};

const trackSuccessfulLogin = (ga: GaTracking | undefined, userData: UserLoginDataResponse) => {
    if (!ga) {
        return;
    }

    const options = {
        action: LOGIN_ACTION,
        label: 'Login successful',
    };

    const category =
        userData?.sessionType === SessionTypes.partial ? PARTIAL_LOGIN_OVERLAY_CATEGORY : LOGIN_OVERLAY_CATEGORY;
    ga.trackGenericEvent(category, options);
};

export const login = (username: string, password: string): LoginAction => ({
    type: LOGIN,
    payload: {
        username,
        password,
    },
});

export const logout = (): LogoutAction => ({
    type: LOGOUT,
});

export const logoutSuccess = (): LogoutSuccessAction => ({
    type: LOGOUT_SUCCESS,
});

export const logoutFailed = (payload: LogoutResponse | Error): LogoutFailedAction => ({
    type: LOGOUT_FAILED,
    payload,
});

export const registerNewUser = (formValues: RegisterUserValues): RegisterNewUserAction => ({
    type: REGISTER_NEW_USER,
    payload: formValues,
});

export const loginWithFacebook = (token: string): LoginWithFacebookAction => ({
    type: LOGIN_WITH_FACEBOOK,
    payload: { token },
});

export const loginSuccess = (response: LoginResponse): DataFetchingThunkAction => async (
    dispatch,
    getState,
    { ga, segmentTracker, apiService }
) => {
    if (response.data.isLoggedIn) {
        dispatch(fetchOrAddProductsWishlist());
        dispatch(fetchOrAddLooksWishlist());

        identifyUser({ userData: response.data, country: apiService.shopApi.country, segmentTracker });
        if (ga) {
            trackUserLoginAndRegistration(response.data, ga);
            dispatch(trackUniversalAnalyticsSessionInit);
        }
    }
    dispatch({
        type: LOGIN_SUCCESS,
        payload: response,
    });

    await dispatch(fetchCartCount());
};

export const serverLoginSuccess = (response: LoginResponse): DataFetchingThunkAction => async dispatch => {
    dispatch({
        type: LOGIN_SUCCESS,
        payload: response,
    });
};

export const setLaunchDarklySessionKey = (id: string): DataFetchingThunkAction => async dispatch => {
    dispatch({
        type: LD_SESSION_ID,
        payload: id,
    });
};

export const clientLoginSuccess = (loginDataState: AppShellLoginState): DataFetchingThunkAction => async (
    dispatch,
    getState,
    { ga, segmentTracker, apiService }
) => {
    const { data: loginData, isLoggedIn } = loginDataState;
    if (isLoggedIn && loginData) {
        dispatch(fetchOrAddProductsWishlist());
        dispatch(fetchOrAddLooksWishlist());

        identifyUser({ userData: loginData, country: apiService.shopApi.country, segmentTracker });
        if (ga) {
            trackUserLoginAndRegistration(loginData, ga);
            dispatch(trackUniversalAnalyticsSessionInit);
        }
    }

    await dispatch(fetchCartCount());
};

export const loginFailed = (error: LoginResponse | Error): LoginFailedAction => ({
    type: LOGIN_FAILED,
    payload: error,
});

export const fetchClubIntegrationPixel = () => ({
    type: FETCH_CLUB_INTEGRATION_PIXEL,
});

export const fetchClubIntegrationSuccessPixel = (response: ClubIntegrationPixelResponse) => ({
    type: FETCH_CLUB_INTEGRATION_PIXEL_SUCCESS,
    payload: response,
});

export const fetchClubIntegrationFailedPixel = (error: Error) => ({
    type: FETCH_CLUB_INTEGRATION_PIXEL_FAILED,
    payload: error,
});

export const isLoggedIn = (): IsLoggedInAction => ({
    type: IS_LOGGED_IN,
});

export const setRedirectUrl = (url: string): SetRedirectUrlAction => ({
    type: SET_REDIRECT_URL,
    payload: { url },
});

export const unSetRedirectUrl = (): UnSetRedirectUrlAction => ({
    type: UNSET_REDIRECT_URL,
});

export const setIsRegisteredWithNewsletter = (): IsRegisteredWithNewsletterActions => ({
    type: SET_IS_REGISTERED_WITH_NEWSLETTER,
});

const responseValidator = (json: any, accessField: string = 'isLoggedIn'): boolean =>
    typeof json === 'object' && json.data && json.data[accessField];

export const getLoggedInUserData = (): DataFetchingThunkAction => async (dispatch, getState, { apiService, ga }) => {
    dispatch(isLoggedIn());
    try {
        const json = await apiService.shopApi.isAuthenticated();
        if (ga && json.data && json.data.isLoggedIn) {
            trackUserLoginAndRegistration(json.data, ga);
            dispatch(trackUniversalAnalyticsSessionInit);
        }
        dispatch(loginSuccess(json));
        dispatch(fetchCartCount());
    } catch (error) {
        dispatch(fetchCartCount());
        dispatch(loginFailed(error));
    }
};

export const apiLogin = (
    username: string,
    password: string,
    fromRegistration: boolean = false
): DataFetchingThunkAction => async (dispatch, _, { apiService, ga }) => {
    dispatch(login(username, password));
    await apiService.shopApi
        .authenticate(username, password)
        .then(json => {
            const loginStatus = responseValidator(json);
            if (fromRegistration) {
                trackLoginViaRegistration(ga, loginStatus);
            }
            if (loginStatus) {
                dispatch(getLoggedInUserData());
                trackSuccessfulLogin(ga, json.data);
                return;
            }
            dispatch(loginFailed(json));
        })
        .catch(err => {
            if (fromRegistration) {
                trackLoginViaRegistration(ga, false);
            }
            dispatch(loginFailed(err));
        });
};

const isValidLogoutResponse = (arg: any): arg is LogoutResponse => {
    return typeof arg === 'object' && typeof arg.success === 'boolean' && typeof arg.data === 'object';
};

export const apiLogout = (): DataFetchingThunkAction => async (dispatch, _, { apiService, segmentTracker }) => {
    dispatch(logout());
    const response = await apiService.shopApi.logout();
    try {
        if (isValidLogoutResponse(response)) {
            if (!response.data.isLoggedIn) {
                dispatch(logoutSuccess());
                dispatch(clearWishlistAction());
                dispatch(clearCartAction());
                segmentTracker.reset();
            } else {
                dispatch(logoutFailed(response));
                // @todo send notification to logout and reload?
            }
        }
    } catch (err) {
        dispatch(logoutFailed(err));
        // @todo send notification to logout and reload?
    }
};

export const apiRegisterNewUser = (formValues: RegisterUserValues): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, ga }
) => {
    dispatch(registerNewUser(formValues));
    await apiService.shopApi
        .register(formValues)
        .then(json => {
            const loginStatus = responseValidator(json);

            if (loginStatus) {
                if (formValues.isNewsletterSubscribed) {
                    dispatch(setIsRegisteredWithNewsletter());
                }
                trackNewRegistration(ga, !!formValues.isNewsletterSubscribed);
                dispatch(getLoggedInUserData());
                return;
            }
            if (json && json.data && json.data.isUserAlreadyExisting) {
                dispatch(apiLogin(formValues.email, formValues.password, true));
                return;
            }
            dispatch(loginFailed(json));
        })
        .catch(err => dispatch(loginFailed(err)));
};

export const handleExistingUserClubRegistration = (
    formValues: RegisterUserValuesEmailPassword,
    existingUserMessage?: string
): DataFetchingThunkAction => async (dispatch, _, { apiService }) => {
    await apiService.shopApi
        .authenticate(formValues.email, formValues.password)
        .then(loginJson => {
            const loginStatus = responseValidator(loginJson);
            if (loginStatus) {
                const isAcceptedNewLetter = !!responseValidator(loginJson, 'accepted_newsletter_confirmed');
                const status = isAcceptedNewLetter
                    ? ClubRegistrationStatus.EXISTING_USER_SUBSCRIBED
                    : ClubRegistrationStatus.EXISTING_USER_NOT_SUBSCRIBED;

                dispatch(getLoggedInUserData());
                dispatch(updateClubRegistrationStatus(status));
                return;
            }

            dispatch(updateClubRegistrationStatus(ClubRegistrationStatus.WRONG_PASSWORD));
            if (existingUserMessage) {
                dispatch(addNotification(existingUserMessage));
            }
            dispatch(loginFailed(loginJson));
        })
        .catch(err => dispatch(loginFailed(err)));
};

export const apiRegisterUserToClub = (
    formValues: RegisterUserValues,
    registrationPayload: { notificationMessages: { newUser: string; existingUser: string }; hasDOIStep: boolean }
): DataFetchingThunkAction => async (dispatch, _, { apiService }) => {
    dispatch(registerNewUser(formValues));
    const {
        notificationMessages: { existingUser, newUser },
    } = registrationPayload;
    await apiService.shopApi
        .register(formValues)
        .then(json => {
            const isAlreadyAUser = responseValidator(json, 'isUserAlreadyExisting');
            const registrationStatus = responseValidator(json);

            // user already exists, let's try do a login
            if (isAlreadyAUser) {
                dispatch(handleExistingUserClubRegistration(formValues, existingUser));
                return;
            }

            if (registrationStatus) {
                dispatch(getLoggedInUserData());
                dispatch(updateClubRegistrationStatus(ClubRegistrationStatus.NEW_REGISTRATION));
                if (!registrationPayload.hasDOIStep) {
                    dispatch(addNotification(newUser));
                }
            } else {
                dispatch(loginFailed(json));
            }
        })
        .catch(err => dispatch(loginFailed(err)));
};

export const apiLoginWithFacebook = (token: string): DataFetchingThunkAction => async (dispatch, _, { apiService }) => {
    dispatch(loginWithFacebook(token));
    await apiService.shopApi
        .loginWithFacebook(token)
        .then(json => {
            const loginStatus = responseValidator(json);
            if (loginStatus) {
                dispatch(getLoggedInUserData());
                return;
            }
            dispatch(loginFailed(json));
        })
        .catch(err => dispatch(loginFailed(err)));
};

export const apiFetchClubIntegrationPixel = (): DataFetchingThunkAction => async (dispatch, _, { apiService }) => {
    dispatch(fetchClubIntegrationPixel());
    await apiService.shopApi
        .getClubIntegrationPixel()
        .then(json => dispatch(fetchClubIntegrationSuccessPixel(json)))
        .catch(err => dispatch(fetchClubIntegrationFailedPixel(err)));
};
